import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import AllInvoices from './AllInvoices';
import AllProposals from './AllProposals';

const tabOptions = [
  { id: 'overview', label: 'Resumen del equipo' },
  { id: 'proposals', label: 'Propuestas' },
  { id: 'invoices', label: 'Facturas' },
];

const AdminSales = () => {
  const [sellers, setSellers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const teamSellerQuery = query(
          collection(db, 'teamMembers'),
          where('role', '==', 'seller')
        );

        const [teamSnap, legacySnap, leadsSnap] = await Promise.all([
          getDocs(teamSellerQuery),
          getDocs(collection(db, 'salesAgents')),
          getDocs(collection(db, 'leads')),
        ]);

        const sellerMap = new Map();
        const pushSeller = (docSnap, data, source) => {
          const key = (data.uid || data.email || docSnap.id || `${source}-${sellerMap.size}`)
            .toString()
            .toLowerCase();
          if (!sellerMap.has(key)) {
            sellerMap.set(key, {
              id: docSnap.id,
              ...data,
            });
          }
        };

        teamSnap.docs.forEach((docSnap) => {
          pushSeller(docSnap, docSnap.data(), 'team');
        });

        legacySnap.docs.forEach((docSnap) => {
          pushSeller(docSnap, docSnap.data(), 'legacy');
        });

        const sellerList = Array.from(sellerMap.values()).sort((a, b) => {
          const nameA = (a.name || a.email || '').toLowerCase();
          const nameB = (b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setSellers(sellerList);
        setLeads(
          leadsSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );
      } catch (err) {
        console.error('❌ Error cargando ventas:', err);
        setMessage('No se pudieron cargar los datos de ventas.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);


  const sellerSummaries = useMemo(() => {
    return sellers.map((seller) => {
      const normalizedEmail = (seller.email || '').toLowerCase();
      const sellerLeads = leads.filter((lead) => {
        const emailMatch = normalizedEmail && (lead.assignedToEmail || '').toLowerCase() === normalizedEmail;
        return (
          lead.assignedTo === seller.id ||
          lead.assignedToUid === seller.uid ||
          emailMatch
        );
      });
      const won = sellerLeads.filter(
        (lead) => (lead.status || '').toLowerCase() === 'convertido'
      );
      const hot = sellerLeads.filter((lead) => (lead.aiScore || 0) >= 80);
      const active = sellerLeads.length - won.length;
      return {
        seller,
        total: sellerLeads.length,
        won: won.length,
        active,
        hot: hot.length,
      };
    });
  }, [sellers, leads]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Ventas & Cotizaciones</h2>
        <p className="text-gray-500">Controla el desempeño del equipo y consulta propuestas / facturas.</p>
      </div>

      <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
        {tabOptions.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'flex-none whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition',
                active
                  ? 'border-transparent bg-emerald-600 text-white shadow'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-400 hover:text-emerald-600',
              ].join(' ')}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-8">
          {message && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded">
              {message}
            </div>
          )}

          <section className="grid gap-4 md:grid-cols-3">
            {sellerSummaries.map(({ seller, total, hot, active, won }) => (
              <div key={seller.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{seller.name}</h3>
                  <span className="text-xs text-gray-500">{seller.email}</span>
                </div>
                <dl className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <dt>Total leads</dt>
                    <dd className="font-semibold text-gray-900">{total}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>En proceso</dt>
                    <dd>{active}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Hot leads</dt>
                    <dd>{hot}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Ganados</dt>
                    <dd className="text-emerald-600 font-semibold">{won}</dd>
                  </div>
                </dl>
              </div>
            ))}
            {sellerSummaries.length === 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-orange-700">
                No se encontraron vendedores activos. Regístralos desde Operaciones → Equipo.
              </div>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Resumen operativo</h3>
            <p className="text-sm text-gray-600">
              La creación de vendedores y la asignación de leads se maneja ahora desde{' '}
              <strong>Operaciones → Equipo</strong> y la vista principal de <strong>Leads</strong>.
              Este módulo se mantiene como tablero de resultados comerciales para cada ejecutivo.
            </p>
            {loading && (
              <p className="text-sm text-gray-500">Cargando datos de ventas...</p>
            )}
          </section>
        </div>
      )}

      {activeTab === 'proposals' && (
        <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
          <AllProposals />
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
          <AllInvoices />
        </div>
      )}
    </div>
  );
};

export default AdminSales;
